import { useState } from 'react'
import { useAdmin } from '../context/AdminContext'

const Header = ({ user, onLogout }) => {
  const { siteMode, updateSiteMode } = useAdmin()
  const [notification, setNotification] = useState(null)
  const [isUpdating, setIsUpdating] = useState(false)

  const handleSiteModeToggle = async () => {
    const newMode = siteMode === 'portfolio' ? 'ecommerce' : 'portfolio'
    setIsUpdating(true)
    
    try {
      const result = await updateSiteMode(newMode)
      
      if (result.success) {
        setNotification({
          type: 'success',
          message: `✅ Site mode changed to ${newMode.toUpperCase()}! All users will see this change when they refresh.`
        })
      } else {
        setNotification({
          type: 'error',
          message: `❌ ${result.message || 'Failed to update site mode'}`
        })
      }
    } catch (error) {
      setNotification({
        type: 'error',
        message: '❌ Network error occurred while updating site mode'
      })
    } finally {
      setIsUpdating(false)
      // Clear notification after 5 seconds
      setTimeout(() => setNotification(null), 5000)
    }
  }

  return (
    <>
      {/* Notification */}
      {notification && (
        <div 
          className={`admin-notification ${notification.type}`}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '1rem 1.5rem',
            borderRadius: '12px',
            background: notification.type === 'success' 
              ? 'linear-gradient(135deg, #00ff88, #00d4ff)' 
              : 'linear-gradient(135deg, #ff006e, #ff4081)',
            color: '#000',
            fontWeight: '600',
            zIndex: 10000,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxWidth: '400px',
            animation: 'slideInRight 0.3s ease-out'
          }}
        >
          {notification.message}
        </div>
      )}

      <header className="admin-header">
        <div className="header-left">
          <h1>🏢 Admin Dashboard</h1>
        </div>
        
        <div className="header-right">
          {/* Site Mode Toggle */}
          <div 
            className={`site-mode-badge ${siteMode} ${isUpdating ? 'updating' : ''}`}
            onClick={!isUpdating ? handleSiteModeToggle : undefined}
            style={{ 
              cursor: isUpdating ? 'not-allowed' : 'pointer',
              opacity: isUpdating ? 0.7 : 1,
              transition: 'all 0.3s ease'
            }}
            title={isUpdating ? "Updating..." : "Click to toggle site mode for all users"}
          >
            {isUpdating ? (
              '⏳ Updating...'
            ) : (
              siteMode === 'portfolio' ? '📋 Portfolio Mode' : '🛒 E-commerce Mode'
            )}
          </div>

          {/* User Info */}
          <div className="user-menu">
            <div className="user-button">
              <span>👨‍💼</span>
              <div>
                <div style={{ fontWeight: '600' }}>{user?.name}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>
                  {user?.role}
                </div>
              </div>
            </div>
          </div>

          {/* Logout */}
          <button className="logout-btn" onClick={onLogout}>
            🚪 Logout
          </button>
        </div>
      </header>

      <style jsx>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .site-mode-badge.updating {
          animation: pulse 1.5s infinite;
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </>
  )
}

export default Header
